import React, { useState, useEffect } from 'react';

import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';
import GET_UNFULFILLED_PARTICIPANT_COACH_PAIRS_QUERY from 'coaching/queries/GetUnfulfilledParticipantCoachPairs.gql';

import BoxBody from 'components/BoxBody';
import { CancellationReasonsSelect } from 'coaching/components/CancellationReasonsSelect';
import Checkbox from 'components/Checkbox';
import CoachingConstants from 'coaching/CoachingConstants';
import ConfirmDialog from 'components/ConfirmDialog';
import {
  adminDeleteCoachingSessionSchedule,
  adminDeleteCoachingSessionInstance,
} from 'coaching/actions/CoachingActions';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Grid from 'components/Grid';
import Radio from 'components/Radio';
import Row from 'components/Row';
import Text from 'components/Text';
import {
  CoachingSessionProps,
  useCoachingSessionState,
  SessionRowHeader,
  SessionRowMetadata,
} from 'coaching/components/CoachingCoordinatorView/SharedCoordinatorSessionRowComponents';
import { wouldBeLateCancellation } from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';

const cancellationReasonNeedsLateConfirmation = (cancellationReason?: string) => {
  // customer-side cancellation reasons we want coordinators to confirm lateness for
  const reasonsNeedingLateConfirmation = [
    'participant_requested_reschedule',
    'help_form_requested',
  ];
  return cancellationReason && reasonsNeedingLateConfirmation.includes(cancellationReason);
};

const eventPrefix = `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.participant_sidebar.remove_session_confirm_dialog`;

function trackEvent(event: string, properties?: Record<string, any>) {
  EventTracking.trackKey(`${eventPrefix}.${event}`, properties);
}

export const ConfirmDeleteDialog = ({
  session,
  participantUuid,
  userName,
  userType,
}: CoachingSessionProps) => {
  const [deleteScheduleFromInstance, setDeleteScheduleFromInstance] = useState(false);
  const {
    setConfirmDeleteDialog,
    confirmDeleteDialog,
    setIsActuallyLateCancellation,
    isActuallyLateCancellation,
  } = useCoachingSessionState();

  useEffect(() => {
    trackEvent('view');
  }, []);

  const deleteCoachingSessionInstanceOrSchedule = () => {
    const callScheduleDeletion = ongoingSeries || oneOffSession || deleteScheduleFromInstance;
    const queryToRefetch = [
      {
        query: GET_COACHING_SESSIONS_QUERY,
        variables: {
          userUuid: participantUuid,
          userType: userType,
        },
      },
      { query: GET_UNFULFILLED_PARTICIPANT_COACH_PAIRS_QUERY, variables: {} },
    ];
    const sharedParams = {
      scheduleUuid: session.uuid,
      cancellationReason: cancellationReason!,
      // this gets passed in as the value of waiveCustomerLateCharge.
      // isActuallyLateCancellation should be defined whenever isLateCancellation
      // is true, but guarding against the possibility it's not here
      waiveCustomerLateCharge: isLateCancellation ? !(isActuallyLateCancellation ?? true) : false,
    };
    return callScheduleDeletion
      ? adminDeleteCoachingSessionSchedule(
          sharedParams,
          deleteScheduleFromInstance ? coachingSessionInstanceUuid : undefined,
          queryToRefetch
        )
      : adminDeleteCoachingSessionInstance(
          sharedParams,
          coachingSessionInstanceUuid!,
          queryToRefetch
        );
  };

  // only render if isLateCancellation is false and attempting to delete an instance
  const renderDeleteAllSessionsPrompt = () => {
    return (
      <Checkbox
        checked={deleteScheduleFromInstance}
        data-test="enable-delete-schedule-from-inst-checkbox"
        label="Remove this and all following sessions"
        onChange={() => {
          setDeleteScheduleFromInstance(!deleteScheduleFromInstance);
          trackEvent('remove_all_following_sessions.click');
        }}
      />
    );
  };

  const renderConfirmLateCancellationPrompt = (ongoingSeries: boolean) => {
    return (
      <BoxBody size="xsmall" dark rounded data-test="confirm-late-cancellation-prompt">
        <Row size="xsmall">
          <Text size="xsmall">
            {ongoingSeries ? 'There' : 'This'} is a session starting within 24 hours. Please confirm
            if this is a late cancellation.
          </Text>
        </Row>
        <Row size="xsmall">
          <Text size="xsmall" color="graphite">
            Select one (required)
          </Text>
        </Row>
        <Row size="small">
          <Grid columns={2}>
            <Radio
              checked={!!isActuallyLateCancellation}
              label="Yes"
              onChange={() => setIsActuallyLateCancellation(true)}
              data-test="yes-late-cancellation-radio"
            />
            <Radio
              // makes the initial state of both Radios unchecked instead of defaulting to one
              checked={
                isActuallyLateCancellation === undefined ? false : !!!isActuallyLateCancellation
              }
              label="No"
              onChange={() => setIsActuallyLateCancellation(false)}
              data-test="not-late-cancellation-radio"
            />
          </Grid>
        </Row>
      </BoxBody>
    );
  };

  if (!confirmDeleteDialog) {
    return null;
  }

  const {
    cancellationReason,
    coachingSessionInstanceUuid,
    instanceDateString,
    instanceStartDateTime,
    ongoingSeries,
  } = confirmDeleteDialog;
  const oneOffSession =
    session.instances.length === 1 && coachingSessionInstanceUuid === session.instances[0].eventId;

  const textConstants = ongoingSeries
    ? CoachingConstants.DELETE_COACHING_SESSION_MODAL_ONGOING
    : CoachingConstants.DELETE_COACHING_SESSION_MODAL_SINGLE;

  // rely on instanceStartDateTime if it's passed in, and the startDateTime of the next instance of the schedule if it's not
  const isLateCancellation = wouldBeLateCancellation(
    instanceStartDateTime ?? session.instances[0].startDateTime
  );
  const needsCancellationLatenessConfirmed =
    isLateCancellation && cancellationReasonNeedsLateConfirmation(cancellationReason);

  const cancellationLatenessConfirmedIfNeeded = needsCancellationLatenessConfirmed
    ? isActuallyLateCancellation !== undefined
    : true;
  const canDeleteCoachingSession = cancellationReason && cancellationLatenessConfirmedIfNeeded;

  return (
    <ConfirmDialog
      data-test="confirm-delete-dialog"
      actionButtonColor="apple"
      cancelButtonColor="apple"
      actionButtonText={textConstants.confirmText}
      confirmEventKey={`${eventPrefix}.remove_session.click`}
      confirmDisabled={!canDeleteCoachingSession}
      cancelButtonText={textConstants.cancelText}
      cancelEventKey={`${eventPrefix}.keep_session.click`}
      preConfirmPromise={deleteCoachingSessionInstanceOrSchedule}
      onRequestHide={() => {
        trackEvent('close.click');
        setConfirmDeleteDialog(undefined);
        setIsActuallyLateCancellation(undefined);
      }}
      size="xsmall"
      title={<Text font="serif">{textConstants.headerText}</Text>}>
      <Text align="left">
        <Row size="xxsmall">
          <Text weight="semibold">{userName}</Text>
        </Row>
        <SessionRowHeader session={session} instanceDateString={instanceDateString} />
        <Row size="xsmall">
          <SessionRowMetadata session={session} />
        </Row>
        {!ongoingSeries && !isLateCancellation && !oneOffSession && renderDeleteAllSessionsPrompt()}
        <Divider size="medium" />
        <Row>
          <CancellationReasonsSelect
            changeState={(value: string) => {
              setConfirmDeleteDialog({
                ...confirmDeleteDialog,
                cancellationReason: value,
              });
              setIsActuallyLateCancellation(undefined);
              trackEvent('cancellation_reason.click');
            }}
            sessionType={
              ongoingSeries || deleteScheduleFromInstance ? 'recurringSchedule' : 'instance'
            }
            userType="coordinator"
          />
        </Row>
        {cancellationReason &&
          needsCancellationLatenessConfirmed &&
          renderConfirmLateCancellationPrompt(ongoingSeries)}
      </Text>
    </ConfirmDialog>
  );
};
